/**
 * Browser-side scrcpy control message serializer.
 *
 * Mirrors src/scrcpy/ControlSerializer.ts byte-for-byte but uses Uint8Array +
 * DataView so it can run in the browser. If you change one, change the other.
 *
 * Wire format anchored to scrcpy v3.3.4 (commit fb6381f).
 */

/* -------------------------------------------------------------------- */
/* Constants                                                            */
/* -------------------------------------------------------------------- */

export const CTRL_TYPE = {
  INJECT_KEYCODE: 0x00,
  INJECT_TEXT: 0x01,
  INJECT_TOUCH_EVENT: 0x02,
  INJECT_SCROLL_EVENT: 0x03,
  BACK_OR_SCREEN_ON: 0x04,
  EXPAND_NOTIFICATION_PANEL: 0x05,
  EXPAND_SETTINGS_PANEL: 0x06,
  COLLAPSE_PANELS: 0x07,
  GET_CLIPBOARD: 0x08,
  SET_CLIPBOARD: 0x09,
  SET_DISPLAY_POWER: 0x0a,
  ROTATE_DEVICE: 0x0b,
} as const;

export const KEY_ACTION = { DOWN: 0, UP: 1 } as const;

export const MOTION_ACTION = {
  DOWN: 0, UP: 1, MOVE: 2, CANCEL: 3, OUTSIDE: 4,
  POINTER_DOWN: 5, POINTER_UP: 6, HOVER_MOVE: 7, SCROLL: 8,
  HOVER_ENTER: 9, HOVER_EXIT: 10, BUTTON_PRESS: 11, BUTTON_RELEASE: 12,
} as const;

export const MOTION_BUTTON = {
  PRIMARY: 1 << 0,
  SECONDARY: 1 << 1,
  TERTIARY: 1 << 2,
  BACK: 1 << 3,
  FORWARD: 1 << 4,
} as const;

export const KEYCODE = {
  HOME: 3,
  BACK: 4,
  DPAD_UP: 19,
  DPAD_DOWN: 20,
  DPAD_LEFT: 21,
  DPAD_RIGHT: 22,
  DPAD_CENTER: 23,
  VOLUME_UP: 24,
  VOLUME_DOWN: 25,
  POWER: 26,
  ENTER: 66,
  DEL: 67,
  MOVE_HOME: 122,
  MOVE_END: 123,
  TAB: 61,
  SPACE: 62,
  ESCAPE: 111,
  FORWARD_DEL: 112,
  COPY: 278,
  PASTE: 279,
  CUT: 277,
  APP_SWITCH: 187,
} as const;

export const COPY_KEY = { NONE: 0, COPY: 1, CUT: 2 } as const;

export const POINTER_ID_MOUSE = 0xffffffffffffffffn;

/* -------------------------------------------------------------------- */
/* Helpers                                                              */
/* -------------------------------------------------------------------- */

const utf8Enc = new TextEncoder();

function floatToU16FixedPoint(value: number): number {
  if (!Number.isFinite(value) || value < 0) return 0;
  if (value >= 1) return 0xffff;
  const scaled = Math.round(value * 0x10000);
  return scaled >= 0xffff ? 0xffff : scaled;
}

function floatToI16FixedPoint(value: number): number {
  if (!Number.isFinite(value)) return 0;
  if (value >= 16) return 0x7fff;
  if (value <= -16) return 0x8000;
  const scaled = Math.round((value / 16) * 0x8000);
  if (scaled >= 0x7fff) return 0x7fff;
  if (scaled <= -0x8000) return 0x8000;
  return scaled & 0xffff;
}

function makeView(len: number): { buf: Uint8Array; dv: DataView } {
  const buf = new Uint8Array(len);
  const dv = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  return { buf, dv };
}

/* -------------------------------------------------------------------- */
/* Serializers                                                          */
/* -------------------------------------------------------------------- */

export function packInjectKeycode(args: {
  action: number;
  keycode: number;
  repeat: number;
  metaState: number;
}): Uint8Array {
  const { buf, dv } = makeView(14);
  buf[0] = CTRL_TYPE.INJECT_KEYCODE;
  buf[1] = args.action & 0xff;
  dv.setInt32(2, args.keycode | 0, false);
  dv.setInt32(6, args.repeat | 0, false);
  dv.setInt32(10, args.metaState | 0, false);
  return buf;
}

export function packInjectText(text: string): Uint8Array {
  const payload = utf8Enc.encode(text);
  if (payload.length > 300) {
    throw new Error(`INJECT_TEXT exceeds 300 bytes (got ${payload.length})`);
  }
  const { buf, dv } = makeView(5 + payload.length);
  buf[0] = CTRL_TYPE.INJECT_TEXT;
  dv.setUint32(1, payload.length, false);
  buf.set(payload, 5);
  return buf;
}

export function packInjectTouch(args: {
  action: number;
  pointerId: bigint;
  x: number;
  y: number;
  screenWidth: number;
  screenHeight: number;
  pressure: number;
  actionButton: number;
  buttons: number;
}): Uint8Array {
  const { buf, dv } = makeView(32);
  buf[0] = CTRL_TYPE.INJECT_TOUCH_EVENT;
  buf[1] = args.action & 0xff;
  dv.setBigInt64(2, args.pointerId, false);
  dv.setInt32(10, args.x | 0, false);
  dv.setInt32(14, args.y | 0, false);
  dv.setUint16(18, args.screenWidth & 0xffff, false);
  dv.setUint16(20, args.screenHeight & 0xffff, false);
  dv.setUint16(22, floatToU16FixedPoint(args.pressure), false);
  dv.setInt32(24, args.actionButton | 0, false);
  dv.setInt32(28, args.buttons | 0, false);
  return buf;
}

export function packInjectScroll(args: {
  x: number;
  y: number;
  screenWidth: number;
  screenHeight: number;
  hscroll: number;
  vscroll: number;
  buttons: number;
}): Uint8Array {
  const { buf, dv } = makeView(21);
  buf[0] = CTRL_TYPE.INJECT_SCROLL_EVENT;
  dv.setInt32(1, args.x | 0, false);
  dv.setInt32(5, args.y | 0, false);
  dv.setUint16(9, args.screenWidth & 0xffff, false);
  dv.setUint16(11, args.screenHeight & 0xffff, false);
  dv.setUint16(13, floatToI16FixedPoint(args.hscroll), false);
  dv.setUint16(15, floatToI16FixedPoint(args.vscroll), false);
  dv.setInt32(17, args.buttons | 0, false);
  return buf;
}

export function packSetClipboard(args: {
  sequence: bigint;
  paste: boolean;
  text: string;
}): Uint8Array {
  const payload = utf8Enc.encode(args.text);
  const { buf, dv } = makeView(14 + payload.length);
  buf[0] = CTRL_TYPE.SET_CLIPBOARD;
  dv.setBigInt64(1, args.sequence, false);
  buf[9] = args.paste ? 1 : 0;
  dv.setUint32(10, payload.length, false);
  buf.set(payload, 14);
  return buf;
}

export function packGetClipboard(copyKey: number): Uint8Array {
  return new Uint8Array([CTRL_TYPE.GET_CLIPBOARD, copyKey & 0xff]);
}

export function packBackOrScreenOn(action: number): Uint8Array {
  return new Uint8Array([CTRL_TYPE.BACK_OR_SCREEN_ON, action & 0xff]);
}

export function packExpandNotificationPanel(): Uint8Array {
  return new Uint8Array([CTRL_TYPE.EXPAND_NOTIFICATION_PANEL]);
}

export function packExpandSettingsPanel(): Uint8Array {
  return new Uint8Array([CTRL_TYPE.EXPAND_SETTINGS_PANEL]);
}

export function packCollapsePanels(): Uint8Array {
  return new Uint8Array([CTRL_TYPE.COLLAPSE_PANELS]);
}

export function packRotateDevice(): Uint8Array {
  return new Uint8Array([CTRL_TYPE.ROTATE_DEVICE]);
}

export function packSetDisplayPower(on: boolean): Uint8Array {
  return new Uint8Array([CTRL_TYPE.SET_DISPLAY_POWER, on ? 1 : 0]);
}
